import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Button,
  Drawer,
  Empty,
  Input,
  InputNumber,
  Modal,
  Select,
  Space,
  Table,
  Tooltip,
  Typography,
  Upload,
  notification,
} from 'antd';
import {
  DownloadOutlined,
  ReloadOutlined,
  ScanOutlined,
  UploadOutlined,
  ClearOutlined,
  PhoneOutlined,
  MailOutlined,
} from '@ant-design/icons';
import { useSearchParams } from 'react-router-dom';
import lib from '../../../assets/utils/lib';
import {
  CONFLICT_CODE_OPTIONS,
  CONFLICT_SEVERITY_OPTIONS,
  CONFLICT_STATUS_OPTIONS,
  RESOLVE_ACTIONS,
  clearOpenConfirmBullets,
  conflictCodeLabel,
  conflictExplanation,
  conflictListPresentation,
  enrollmentConflictsEmptyText,
  fieldLabel,
  formatClearOpenFeedback,
  formatResolveFeedback,
  hypothesisHints,
  isActionRecommended,
  isEnrollmentSchemaMissing,
  isHeadsReduction,
  isIdentityMatchConflict,
  linkablePeople,
  liveSnapshotEnvelope,
  personCardFields,
  personDisplayName,
  personMatchContact,
  recommendedActions,
  resolveActionTone,
  resolveActionWhy,
  resolvePlainOrgCode,
  severityLabel,
  severityTone,
  snapshotFieldPairs,
  stagingDisplayName,
  topDiffs,
} from '../enrollmentConflictHelpers';
import './enrollment-conflicts.css';

const { Text, Paragraph } = Typography;
const { TextArea } = Input;

function formatVal(value) {
  if (value == null || value === '') return '—';
  if (typeof value === 'object') return JSON.stringify(value);
  return String(value);
}

function PriorityChip({ severity }) {
  const tone = severityTone(severity);
  return (
    <span className={`enrollment-conflict-priority is-${tone}`}>
      <span className="enrollment-conflict-priority__dot" aria-hidden />
      {severityLabel(severity)}
    </span>
  );
}

function SituationBanner({ explanation, tone = 'warning' }) {
  if (!explanation) return null;
  return (
    <div className={`enrollment-conflict-situation${tone === 'info' ? ' is-info' : ''}`}>
      <div className="enrollment-conflict-situation__eyebrow">What happened</div>
      <h3 className="enrollment-conflict-situation__title">{explanation.title}</h3>
      <p className="enrollment-conflict-situation__body">{explanation.body}</p>
    </div>
  );
}

function PersonCard({
  eyebrow,
  pathHint,
  person,
  accent,
  highlightField,
}) {
  const fields = personCardFields(person || {}, { highlight: highlightField });
  return (
    <div className={`enrollment-conflict-person-card${accent ? ` is-${accent}` : ''}`}>
      {eyebrow ? <div className="enrollment-conflict-person-card__eyebrow">{eyebrow}</div> : null}
      {pathHint ? <div className="enrollment-conflict-person-card__path">{pathHint}</div> : null}
      <div className="enrollment-conflict-person-card__name">
        {personDisplayName(person)}
      </div>
      {fields.length ? (
        <div className="enrollment-conflict-person-card__fields">
          {fields.map((f) => (
            <div
              key={f.field}
              className={`enrollment-conflict-person-card__row${f.highlight ? ' is-highlight' : ''}`}
            >
              <span className="enrollment-conflict-person-card__label">{f.label}</span>
              <span>{formatVal(f.value)}</span>
            </div>
          ))}
        </div>
      ) : (
        <div className="enrollment-conflict-person-card__empty">No details available</div>
      )}
    </div>
  );
}

function PathCue({ kind, contact }) {
  return (
    <div className={`enrollment-conflict-path-cue is-${kind}`}>
      <span className="enrollment-conflict-path-cue__icon" aria-hidden>
        {kind === 'phone' ? <PhoneOutlined /> : <MailOutlined />}
      </span>
      <span className="enrollment-conflict-path-cue__text">
        {kind === 'phone' ? 'Matched by phone' : 'Matched by email'}
        {contact ? <span className="enrollment-conflict-path-cue__contact">{contact}</span> : null}
      </span>
    </div>
  );
}

function IdentityConflictView({ conflict }) {
  const staging = conflict?.staging_snapshot || {};
  const env = liveSnapshotEnvelope(conflict);
  const explanation = conflictExplanation(conflict);
  const phoneContact = personMatchContact(conflict, 'phone');
  const emailContact = personMatchContact(conflict, 'email');

  if (env.kind === 'cross') {
    return (
      <div className="enrollment-conflict-identity">
        <SituationBanner explanation={explanation} />
        <p className="enrollment-conflict-board__label">Compare people</p>
        <div className="enrollment-conflict-board enrollment-conflict-board--cross">
          <div className="enrollment-conflict-board__col">
            <div className="enrollment-conflict-path-cue is-upload">
              <span className="enrollment-conflict-path-cue__text">
                Source row
                <span className="enrollment-conflict-path-cue__contact">From this enrollment file</span>
              </span>
            </div>
            <PersonCard
              eyebrow="Upload"
              pathHint={null}
              person={staging}
              accent="upload"
            />
          </div>
          <div className="enrollment-conflict-board__col">
            <PathCue kind="phone" contact={phoneContact} />
            <PersonCard
              eyebrow="Phone match"
              pathHint="Existing person sharing this phone"
              person={env.phoneMatches[0]}
              accent="phone"
              highlightField="phone"
            />
          </div>
          <div className="enrollment-conflict-board__col">
            <PathCue kind="email" contact={emailContact} />
            <PersonCard
              eyebrow="Email match"
              pathHint="Existing person sharing this email"
              person={env.emailMatches[0]}
              accent="email"
              highlightField="email"
            />
          </div>
        </div>
        {(env.phoneMatches.length > 1 || env.emailMatches.length > 1) ? (
          <p className="enrollment-conflict-board__note">
            {env.phoneMatches.length > 1
              ? `${env.phoneMatches.length} people share this phone — showing the first match. `
              : ''}
            {env.emailMatches.length > 1
              ? `${env.emailMatches.length} people share this email — showing the first match.`
              : ''}
          </p>
        ) : null}
      </div>
    );
  }

  if (env.kind === 'ambiguous') {
    return (
      <div className="enrollment-conflict-identity enrollment-conflict-board--ambiguous">
        <SituationBanner explanation={explanation} />
        <div className="enrollment-conflict-identity__upload">
          <p className="enrollment-conflict-board__label">Upload row</p>
          <PersonCard
            eyebrow="Upload"
            pathHint="From this enrollment file"
            person={staging}
            accent="upload"
          />
        </div>
        <p className="enrollment-conflict-candidates-label">
          Possible matches on file ({env.candidates.length})
        </p>
        <div className="enrollment-conflict-identity__grid--candidates">
          {env.candidates.map((person, idx) => (
            <PersonCard
              key={person.id || idx}
              eyebrow={`Person ${idx + 1}`}
              pathHint={person.policy_no ? `Policy ${person.policy_no}` : 'Existing enrollment'}
              person={person}
              accent="candidate"
            />
          ))}
        </div>
      </div>
    );
  }

  return null;
}

function ResolveActionCards({
  conflict,
  value,
  onChange,
  primaryActions,
  moreActions,
}) {
  const renderAction = (action) => {
    const selected = value === action.value;
    const tone = action.tone || 'neutral';
    const why = resolveActionWhy(conflict, action.value);
    return (
      <button
        key={action.value}
        type="button"
        className={[
          'enrollment-conflict-action',
          selected ? 'is-selected' : '',
          tone === 'destructive' ? 'is-destructive' : '',
          tone === 'primary' ? 'is-primary' : '',
        ].filter(Boolean).join(' ')}
        onClick={() => onChange(action.value)}
        aria-pressed={selected}
      >
        <div className="enrollment-conflict-action__row">
          <span className="enrollment-conflict-action__radio" aria-hidden>
            <span className="enrollment-conflict-action__radio-dot" />
          </span>
          <div className="enrollment-conflict-action__body">
            <div className="enrollment-conflict-action__label">{action.label}</div>
            {why ? <div className="enrollment-conflict-action__why">{why}</div> : null}
          </div>
        </div>
      </button>
    );
  };

  return (
    <div className="enrollment-conflict-actions">
      {primaryActions.length ? (
        <>
          <div className="enrollment-conflict-actions__group-label">Recommended</div>
          {primaryActions.map(renderAction)}
        </>
      ) : null}
      {moreActions.length ? (
        <>
          <div className="enrollment-conflict-actions__group-label">
            {primaryActions.length ? 'Other options' : 'Options'}
          </div>
          {moreActions.map(renderAction)}
        </>
      ) : null}
    </div>
  );
}

function parseReuploadCsv(text) {
  const lines = String(text || '').split(/\r?\n/).filter((l) => l.trim());
  if (lines.length < 2) return [];
  const headers = lines[0].split(',').map((h) => h.trim().replace(/^"|"$/g, ''));
  return lines.slice(1).map((line) => {
    const cols = line.split(',').map((c) => c.trim().replace(/^"|"$/g, ''));
    const row = {};
    headers.forEach((h, i) => {
      row[h] = cols[i] ?? '';
    });
    const sessionRowId = row.session_row_id || row.sessionRowId;
    return {
      session_row_id: sessionRowId ? Number(sessionRowId) || sessionRowId : undefined,
      name: row.name || undefined,
      phone: row.phone || undefined,
      email: row.email || undefined,
      insurance_package: row.insurance_package || undefined,
      additional_heads: row.additional_heads !== '' && row.additional_heads != null
        ? Number(row.additional_heads)
        : undefined,
      premium_type: row.premium_type || undefined,
      state: row.state || undefined,
      city: row.city || undefined,
    };
  }).filter((r) => r.session_row_id || r.phone || r.email);
}

export default function EnrollmentConflictsPanel({
  defaultSocialNo = '',
  defaultOrganizationId = null,
  showScan = true,
  onCountsChange,
}) {
  const [searchParams, setSearchParams] = useSearchParams();
  const sessionIdParam = searchParams.get('session_id') || '';
  const socialNoParam = resolvePlainOrgCode(
    searchParams.get('social_no'),
    defaultSocialNo,
  );
  const orgIdParam = searchParams.get('organization_id') || defaultOrganizationId || '';

  const [status, setStatus] = useState('open');
  const [severity, setSeverity] = useState('');
  const [conflictCode, setConflictCode] = useState('');
  const [sessionId, setSessionId] = useState(sessionIdParam);
  const [socialNo, setSocialNo] = useState(socialNoParam);
  const [rows, setRows] = useState([]);
  const [meta, setMeta] = useState({ total: 0, page: 1, pageSize: 20 });
  const [loading, setLoading] = useState(false);
  const [schemaMissing, setSchemaMissing] = useState(false);
  const [schemaMessage, setSchemaMessage] = useState('');
  const [selected, setSelected] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [resolveAction, setResolveAction] = useState(null);
  const [resolveNote, setResolveNote] = useState('');
  const [clientId, setClientId] = useState(null);
  const [resolving, setResolving] = useState(false);
  const [sessionInfo, setSessionInfo] = useState(null);
  const [exporting, setExporting] = useState(false);
  const [scanning, setScanning] = useState(false);
  const [reuploading, setReuploading] = useState(false);
  const [clearing, setClearing] = useState(false);

  useEffect(() => {
    setSessionId(sessionIdParam);
  }, [sessionIdParam]);

  useEffect(() => {
    setSocialNo(socialNoParam);
  }, [socialNoParam]);

  // Rewrite legacy links that passed base64 route hashes into ?social_no=
  useEffect(() => {
    const raw = searchParams.get('social_no');
    if (!raw) return;
    const plain = resolvePlainOrgCode(raw);
    if (!plain || plain === raw) return;
    const next = new URLSearchParams(searchParams);
    next.set('social_no', plain);
    setSearchParams(next, { replace: true });
  }, [searchParams, setSearchParams]);

  const listParams = useMemo(() => ({
    status,
    page: meta.page,
    pageSize: meta.pageSize,
    ...(severity ? { severity } : {}),
    ...(conflictCode ? { conflict_code: conflictCode } : {}),
    ...(sessionId ? { session_id: sessionId } : {}),
    ...(socialNo ? { social_no: socialNo } : {}),
    ...(orgIdParam ? { organization_id: orgIdParam } : {}),
  }), [status, severity, conflictCode, sessionId, socialNo, orgIdParam, meta.page, meta.pageSize]);

  const loadList = useCallback(async (page = meta.page, pageSize = meta.pageSize) => {
    setLoading(true);
    try {
      const parsed = lib.parseApiEnvelope(await lib.getEnrollmentConflicts({
        ...listParams,
        page,
        pageSize,
      }));
      if (isEnrollmentSchemaMissing(parsed)) {
        setSchemaMissing(true);
        setSchemaMessage(parsed.message || 'Enrollment conflict schema not migrated');
        setRows([]);
        setMeta((prev) => ({ ...prev, total: 0, page, pageSize }));
        return;
      }
      setSchemaMissing(false);
      if (!parsed.ok) {
        notification.error({ message: parsed.message || 'Failed to load conflicts' });
        setRows([]);
        return;
      }
      const data = Array.isArray(parsed.data) ? parsed.data : (parsed.data?.conflicts || []);
      setRows(data);
      const m = parsed.meta || {};
      setMeta({
        total: Number(m.total) || data.length,
        page: Number(m.page) || page,
        pageSize: Number(m.pageSize) || pageSize,
      });
      onCountsChange?.();
    } catch (err) {
      notification.error({ message: err?.message || 'Failed to load conflicts' });
    } finally {
      setLoading(false);
    }
  }, [listParams, meta.page, meta.pageSize, onCountsChange]);

  const loadSession = useCallback(async (id) => {
    if (!id) {
      setSessionInfo(null);
      return;
    }
    const parsed = lib.parseApiEnvelope(await lib.getEnrollmentConflictSession(id));
    if (parsed.ok) setSessionInfo(parsed.data);
    else setSessionInfo(null);
  }, []);

  useEffect(() => {
    loadList(1, meta.pageSize);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [status, severity, conflictCode, sessionId, socialNo, orgIdParam]);

  useEffect(() => {
    loadSession(sessionId);
  }, [sessionId, loadSession]);

  const openDetail = async (row) => {
    setSelected(row);
    setResolveNote('');
    setClientId(null);
    const recommended = recommendedActions(row);
    setResolveAction(recommended[0] || 'discard_staging');
    setDetailLoading(true);
    try {
      const parsed = lib.parseApiEnvelope(await lib.getEnrollmentConflict(row.id));
      if (parsed.ok && parsed.data) {
        const detail = parsed.data.conflict || parsed.data;
        setSelected(detail);
        const rec = recommendedActions(detail);
        setResolveAction(rec[0] || 'discard_staging');
      }
    } finally {
      setDetailLoading(false);
    }
  };

  const handleResolve = async () => {
    if (!selected?.id || !resolveAction) return;
    const actionMeta = RESOLVE_ACTIONS.find((a) => a.value === resolveAction);
    if (actionMeta?.requiresClientId && !clientId) {
      notification.warning({ message: 'Choose which person this upload belongs to' });
      return;
    }
    if (resolveAction === 'apply_staging_to_policy' && isHeadsReduction(selected)) {
      const ok = await new Promise((resolve) => {
        Modal.confirm({
          title: 'Heads reduction',
          content: 'Staging has fewer additional heads than the live policy. Apply anyway?',
          okText: 'Apply anyway',
          okButtonProps: { danger: true },
          onOk: () => resolve(true),
          onCancel: () => resolve(false),
        });
      });
      if (!ok) return;
    }

    setResolving(true);
    try {
      const payload = { action: resolveAction, note: resolveNote || undefined };
      if (clientId) payload.client_id = Number(clientId);
      const parsed = lib.parseApiEnvelope(await lib.resolveEnrollmentConflict(selected.id, payload));
      if (!parsed.ok) {
        notification.error({
          key: 'notification',
          message: 'Could not resolve conflict',
          description: parsed.message || 'The server rejected this action.',
          duration: 6,
        });
        return;
      }
      const feedback = formatResolveFeedback(parsed.data, selected, resolveAction);
      notification[feedback.level]({
        message: feedback.message,
        description: feedback.description,
        duration: feedback.level === 'warning' ? 8 : 5,
      });
      setSelected(null);
      await Promise.all([loadList(meta.page, meta.pageSize), loadSession(sessionId)]);
      onCountsChange?.();
    } finally {
      setResolving(false);
    }
  };

  const handleExport = async () => {
    setExporting(true);
    try {
      await lib.exportEnrollmentConflictsCsv({
        session_id: sessionId || undefined,
        social_no: socialNo || undefined,
        organization_id: orgIdParam || undefined,
        status: status === 'all' ? 'open' : status,
        conflict_code: conflictCode || undefined,
        severity: severity || undefined,
      });
      notification.success({ message: 'Conflict CSV downloaded' });
    } catch (err) {
      notification.error({ message: err?.message || 'Export failed' });
    } finally {
      setExporting(false);
    }
  };

  const handleScan = async () => {
    if (!socialNo) {
      notification.warning({ message: 'Enter an organization code to scan' });
      return;
    }
    setScanning(true);
    try {
      const parsed = lib.parseApiEnvelope(await lib.scanEnrollmentConflicts({ social_no: socialNo }));
      if (isEnrollmentSchemaMissing(parsed)) {
        setSchemaMissing(true);
        setSchemaMessage(parsed.message);
        return;
      }
      if (!parsed.ok) {
        notification.error({ message: parsed.message || 'Scan failed' });
        return;
      }
      notification.success({
        message: 'Scan complete',
        description: parsed.message || 'Inbox refreshed with any shadow deals found.',
      });
      await loadList(1, meta.pageSize);
      onCountsChange?.();
    } finally {
      setScanning(false);
    }
  };

  const runClearOpen = async () => {
    setClearing(true);
    try {
      const parsed = lib.parseApiEnvelope(await lib.clearEnrollmentConflicts({
        social_no: socialNo,
        confirm: true,
        action: 'discard_staging',
        close_sessions: true,
        apply_remaining_held: true,
        archive_pending_user_data: false,
        note: 'care_admin_bulk_clear',
      }));
      if (isEnrollmentSchemaMissing(parsed)) {
        setSchemaMissing(true);
        setSchemaMessage(parsed.message);
        throw new Error(parsed.message || 'Enrollment conflict schema not migrated');
      }
      if (!parsed.ok) {
        notification.error({
          key: 'notification',
          message: 'Could not clear open holds',
          description: parsed.message || 'The server rejected the bulk clear request.',
          duration: 8,
        });
        throw new Error(parsed.message || 'Clear failed');
      }
      const feedback = formatClearOpenFeedback(parsed.data, socialNo);
      notification[feedback.level]({
        message: feedback.message,
        description: feedback.description || parsed.message,
        duration: feedback.level === 'warning' ? 10 : 6,
      });
      if (feedback.failed?.length) {
        notification.warning({
          message: `${feedback.failed.length} conflict${feedback.failed.length === 1 ? '' : 's'} could not be cleared`,
          description: feedback.failed
            .slice(0, 4)
            .map((f) => `#${f.conflict_id}: ${f.error}`)
            .join(' · '),
          duration: 12,
        });
      }
      await Promise.all([loadList(1, meta.pageSize), loadSession(sessionId)]);
      onCountsChange?.();
    } catch (err) {
      if (!err?.message || err.message === 'Clear failed') {
        /* already notified */
      } else if (!String(err.message).includes('schema not migrated')) {
        notification.error({
          key: 'notification',
          message: 'Could not clear open holds',
          description: err.message,
          duration: 8,
        });
      }
      throw err;
    } finally {
      setClearing(false);
    }
  };

  const handleClearOpen = () => {
    if (!socialNo) {
      notification.warning({ message: 'Enter an organization code to clear open holds' });
      return;
    }
    Modal.confirm({
      title: `Clear all open holds for ${socialNo}?`,
      content: (
        <div>
          <Paragraph style={{ marginBottom: 8 }}>
            This bulk action affects every open hold for this organization code — not just rows on the current page.
          </Paragraph>
          <ul style={{ marginBottom: 0, paddingLeft: 20 }}>
            {clearOpenConfirmBullets().map((line) => (
              <li key={line}><Text>{line}</Text></li>
            ))}
          </ul>
        </div>
      ),
      okText: 'Clear open holds',
      okButtonProps: { danger: true },
      cancelText: 'Cancel',
      onOk: runClearOpen,
    });
  };

  const handleReupload = async (file) => {
    if (!sessionId) {
      notification.warning({ message: 'Set a session_id before re-uploading conflict rows' });
      return false;
    }
    setReuploading(true);
    try {
      const text = await file.text();
      const parsedRows = parseReuploadCsv(text);
      if (!parsedRows.length) {
        notification.error({ message: 'No conflict rows found in CSV (need session_row_id / phone / email)' });
        return false;
      }
      const parsed = lib.parseApiEnvelope(
        await lib.reuploadEnrollmentConflictSession(sessionId, parsedRows),
      );
      if (!parsed.ok) {
        notification.error({ message: parsed.message || 'Re-upload failed' });
        return false;
      }
      const sessionApply = parsed.data?.session_apply;
      if (sessionApply?.status === 'completed') {
        notification.success({
          message: 'Upload completed',
          description: `${sessionApply.applied_count ?? 0} row(s) applied.`,
        });
      } else {
        notification.success({
          message: 'Conflict rows re-uploaded',
          description: parsed.message || `${parsedRows.length} row(s) submitted`,
        });
      }
      await Promise.all([loadList(1, meta.pageSize), loadSession(sessionId)]);
      onCountsChange?.();
    } catch (err) {
      notification.error({ message: err?.message || 'Re-upload failed' });
    } finally {
      setReuploading(false);
    }
    return false;
  };

  const syncUrlFilters = () => {
    const plainSocialNo = resolvePlainOrgCode(socialNo) || socialNo;
    if (plainSocialNo !== socialNo) setSocialNo(plainSocialNo);
    const next = new URLSearchParams();
    if (sessionId) next.set('session_id', sessionId);
    if (plainSocialNo) next.set('social_no', plainSocialNo);
    if (orgIdParam) next.set('organization_id', String(orgIdParam));
    setSearchParams(next, { replace: true });
  };

  const columns = [
    {
      title: 'Person / row',
      key: 'name',
      render: (_, row) => {
        const presentation = conflictListPresentation(row);
        return (
          <div>
            <div className="enrollment-conflict-row__title">{presentation.title}</div>
            {presentation.subtitle ? (
              <div className="enrollment-conflict-row__subtitle">{presentation.subtitle}</div>
            ) : null}
            <div className="enrollment-conflict-row__meta">{presentation.detailLine}</div>
          </div>
        );
      },
    },
    {
      title: 'What happened',
      dataIndex: 'conflict_code',
      key: 'conflict_code',
      width: 200,
      render: (_, row) => {
        const presentation = conflictListPresentation(row);
        const identity = isIdentityMatchConflict(row);
        return (
          <div className="enrollment-conflict-row__type">
            <span className={`enrollment-conflict-row__type-label${identity ? ' is-identity' : ''}`}>
              {presentation.typeLabel}
            </span>
          </div>
        );
      },
    },
    {
      title: 'Priority',
      dataIndex: 'severity',
      key: 'severity',
      width: 140,
      render: (sev) => <PriorityChip severity={sev} />,
    },
    {
      title: 'Differences',
      key: 'diffs',
      render: (_, row) => {
        if (isIdentityMatchConflict(row)) {
          const presentation = conflictListPresentation(row);
          return (
            <div className="enrollment-conflict-row__diffs">
              {presentation.subtitle || 'Identity needs a human decision'}
            </div>
          );
        }
        const diffs = topDiffs(row);
        if (!diffs.length) return <Text type="secondary">—</Text>;
        return (
          <div className="enrollment-conflict-row__diffs">
            {diffs.map((d) => (
              <div key={d.field || d.name} className="enrollment-conflict-row__diffs-line">
                <Text type="secondary">{fieldLabel(d.field || d.name)}:</Text>{' '}
                {formatVal(d.staging ?? d.upload)} → {formatVal(d.live ?? d.policy)}
              </div>
            ))}
          </div>
        );
      },
    },
    {
      title: 'Notes',
      key: 'hypotheses',
      width: 200,
      render: (_, row) => {
        if (isIdentityMatchConflict(row)) {
          return (
            <div className="enrollment-conflict-row__note">
              Choose the right person or reject
            </div>
          );
        }
        const hints = hypothesisHints(row);
        if (!hints.length) return '—';
        return (
          <Tooltip title={hints.join(' · ')}>
            <div className="enrollment-conflict-row__note">{hints[0]}</div>
          </Tooltip>
        );
      },
    },
    {
      title: '',
      key: 'actions',
      width: 90,
      render: (_, row) => (
        <Button type="link" size="small" onClick={() => openDetail(row)}>
          Resolve
        </Button>
      ),
    },
  ];

  const staging = selected?.staging_snapshot || {};
  const live = selected?.live_snapshot || {};
  const pairs = selected && !isIdentityMatchConflict(selected) ? snapshotFieldPairs(staging, live) : [];
  const moreActions = RESOLVE_ACTIONS.filter((a) => !isActionRecommended(selected, a.value));
  const primaryActions = RESOLVE_ACTIONS.filter((a) => isActionRecommended(selected, a.value));
  const peopleOptions = selected ? linkablePeople(selected) : [];
  const selectedActionMeta = RESOLVE_ACTIONS.find((a) => a.value === resolveAction);
  const selectedTone = resolveActionTone(resolveAction);
  const explanation = selected ? conflictExplanation(selected) : null;

  return (
    <div className="enrollment-conflicts-panel">
      {schemaMissing ? (
        <Alert
          type="error"
          showIcon
          message="Enrollment conflicts unavailable"
          description={schemaMessage || 'Run sqls/migrations/20260729_enrollment_conflicts.sql on this environment.'}
          style={{ marginBottom: 16 }}
        />
      ) : null}

      {sessionInfo?.session ? (
        <Alert
          type={sessionInfo.session.status === 'completed' ? 'success' : 'info'}
          showIcon
          style={{ marginBottom: 16 }}
          message={`Session #${sessionInfo.session.id} — ${sessionInfo.session.status}`}
          description={
            <span>
              Open conflicts: {sessionInfo.open_conflicts ?? sessionInfo.session.open_conflict_count ?? 0}
              {sessionInfo.row_status_counts
                ? ` · held ${sessionInfo.row_status_counts.held || 0} · conflict ${sessionInfo.row_status_counts.conflict || 0} · dropped ${sessionInfo.row_status_counts.dropped || 0}`
                : ''}
              {sessionInfo.can_complete ? ' · ready to complete' : ''}
            </span>
          }
        />
      ) : null}

      <div className="enrollment-conflicts-toolbar">
        <Space wrap>
          <Select
            value={status}
            onChange={setStatus}
            options={CONFLICT_STATUS_OPTIONS}
            style={{ width: 120 }}
            size="small"
          />
          <Select
            value={severity}
            onChange={setSeverity}
            options={CONFLICT_SEVERITY_OPTIONS}
            style={{ width: 150 }}
            size="small"
          />
          <Select
            value={conflictCode}
            onChange={setConflictCode}
            options={CONFLICT_CODE_OPTIONS}
            style={{ width: 260 }}
            size="small"
          />
          <Input
            size="small"
            placeholder="Session ID"
            value={sessionId}
            onChange={(e) => setSessionId(e.target.value.trim())}
            onBlur={syncUrlFilters}
            onPressEnter={syncUrlFilters}
            style={{ width: 120 }}
            allowClear
          />
          <Input
            size="small"
            placeholder="Organization code"
            value={socialNo}
            onChange={(e) => setSocialNo(e.target.value.trim())}
            onBlur={syncUrlFilters}
            onPressEnter={syncUrlFilters}
            style={{ width: 140 }}
            allowClear
          />
        </Space>
        <Space wrap>
          <Button size="small" icon={<ReloadOutlined />} onClick={() => loadList(meta.page, meta.pageSize)} loading={loading}>
            Refresh
          </Button>
          <Button size="small" icon={<DownloadOutlined />} onClick={handleExport} loading={exporting} disabled={schemaMissing}>
            Download CSV
          </Button>
          <Upload accept=".csv,text/csv" showUploadList={false} beforeUpload={handleReupload} disabled={!sessionId || schemaMissing}>
            <Button size="small" icon={<UploadOutlined />} loading={reuploading} disabled={!sessionId || schemaMissing}>
              Re-upload conflicts
            </Button>
          </Upload>
          {showScan ? (
            <Button size="small" icon={<ScanOutlined />} onClick={handleScan} loading={scanning} disabled={!socialNo || schemaMissing}>
              Scan org deals
            </Button>
          ) : null}
          <Button
            size="small"
            danger
            icon={<ClearOutlined />}
            onClick={handleClearOpen}
            loading={clearing}
            disabled={!socialNo || schemaMissing || clearing}
          >
            Clear open holds
          </Button>
        </Space>
      </div>

      <Table
        className="enrollment-conflicts-table"
        rowKey={(r) => r.id}
        columns={columns}
        dataSource={rows}
        loading={loading}
        size="small"
        locale={{
          emptyText: (
            <Empty
              description={enrollmentConflictsEmptyText({
                schemaMissing,
                status,
                socialNo,
                sessionId,
              })}
            />
          ),
        }}
        pagination={{
          current: meta.page,
          pageSize: meta.pageSize,
          total: meta.total,
          showSizeChanger: true,
          onChange: (page, pageSize) => {
            setMeta((prev) => ({ ...prev, page, pageSize }));
            loadList(page, pageSize);
          },
          showTotal: (total, range) => `${range[0]}-${range[1]} of ${total}`,
        }}
      />

      <Drawer
        rootClassName="enrollment-conflict-drawer"
        title={
          selected
            ? (
              <div className="enrollment-conflict-drawer__title">
                <div className="enrollment-conflict-drawer__title-name">{stagingDisplayName(selected)}</div>
                <div className="enrollment-conflict-drawer__title-sub">
                  {conflictCodeLabel(selected.conflict_code)}
                  {selected.id ? ` · Hold #${selected.id}` : ''}
                </div>
              </div>
            )
            : 'Enrollment hold'
        }
        open={Boolean(selected)}
        onClose={() => setSelected(null)}
        width={isIdentityMatchConflict(selected) ? 960 : 720}
        destroyOnClose
        extra={
          selected ? (
            <span className="enrollment-conflict-drawer__priority">
              <PriorityChip severity={selected.severity} />
            </span>
          ) : null
        }
      >
        {detailLoading ? (
          <div className="enrollment-conflict-loading">Loading details…</div>
        ) : null}
        {selected && !detailLoading ? (
          <>
            {isIdentityMatchConflict(selected) ? (
              <IdentityConflictView conflict={selected} />
            ) : (
              <>
                <SituationBanner explanation={explanation} tone="info" />
                <div className="enrollment-conflict-compare">
                  <div className="enrollment-conflict-compare__col">
                    <span className="enrollment-conflict-compare__heading">From the upload</span>
                    {pairs.map((p) => (
                      <div key={`s-${p.field}`} className={`enrollment-conflict-field${p.differed ? ' is-diff' : ''}`}>
                        <span className="enrollment-conflict-field__label">{p.label || fieldLabel(p.field)}</span>
                        <span>{formatVal(p.staging)}</span>
                      </div>
                    ))}
                  </div>
                  <div className="enrollment-conflict-compare__col">
                    <span className="enrollment-conflict-compare__heading">On the live record</span>
                    {pairs.map((p) => (
                      <div key={`l-${p.field}`} className={`enrollment-conflict-field${p.differed ? ' is-diff' : ''}`}>
                        <span className="enrollment-conflict-field__label">{p.label || fieldLabel(p.field)}</span>
                        <span>{formatVal(p.live)}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </>
            )}

            {hypothesisHints(selected).length ? (
              <div className="enrollment-conflict-block">
                <Text strong className="enrollment-conflict-block__heading">Why this was flagged</Text>
                <ul>
                  {hypothesisHints(selected).map((h) => <li key={h}>{h}</li>)}
                </ul>
              </div>
            ) : null}

            {Array.isArray(selected.events) && selected.events.length ? (
              <div className="enrollment-conflict-block enrollment-conflict-block--muted">
                <Text strong type="secondary" className="enrollment-conflict-block__heading">Activity</Text>
                <ul>
                  {selected.events.slice(0, 8).map((ev, idx) => (
                    <li key={ev.id || idx}>
                      {ev.event_type || ev.action || 'update'}
                      {ev.created_at ? ` · ${ev.created_at}` : ''}
                      {ev.note ? ` — ${ev.note}` : ''}
                    </li>
                  ))}
                </ul>
              </div>
            ) : null}

            <div className="enrollment-conflict-block enrollment-conflict-resolve">
              <h3 className="enrollment-conflict-resolve__heading">What should we do?</h3>
              <p className="enrollment-conflict-resolve__lead">
                {isIdentityMatchConflict(selected)
                  ? 'Pick one option. Recommended choices are listed first — destructive options are marked clearly.'
                  : `${conflictCodeLabel(selected.conflict_code)} — choose how to finish this hold.`}
              </p>

              <ResolveActionCards
                conflict={selected}
                value={resolveAction}
                onChange={setResolveAction}
                primaryActions={primaryActions}
                moreActions={moreActions}
              />

              <div className="enrollment-conflict-resolve__fields">
                {selectedActionMeta?.requiresClientId ? (
                  peopleOptions.length ? (
                    <div>
                      <span className="enrollment-conflict-resolve__field-label">
                        Which person does this upload belong to?
                      </span>
                      <Select
                        style={{ width: '100%' }}
                        placeholder="Select the correct person"
                        value={clientId || undefined}
                        onChange={setClientId}
                        options={peopleOptions.map((p) => ({
                          value: p.id,
                          label: p.description ? `${p.label} — ${p.description}` : p.label,
                        }))}
                      />
                    </div>
                  ) : (
                    <div>
                      <span className="enrollment-conflict-resolve__field-label">
                        Live record ID
                      </span>
                      <InputNumber
                        style={{ width: '100%' }}
                        placeholder="Live record ID"
                        value={clientId}
                        onChange={setClientId}
                        min={1}
                      />
                    </div>
                  )
                ) : null}
                <div>
                  <span className="enrollment-conflict-resolve__field-label">
                    Optional note
                  </span>
                  <TextArea
                    rows={2}
                    placeholder="Optional note for the audit trail"
                    value={resolveNote}
                    onChange={(e) => setResolveNote(e.target.value)}
                  />
                </div>
              </div>

              {isHeadsReduction(selected) && resolveAction === 'apply_staging_to_policy' ? (
                <Alert
                  type="warning"
                  showIcon
                  style={{ marginBottom: 12 }}
                  message="The upload has fewer additional heads than the live record"
                />
              ) : null}

              <div className="enrollment-conflict-resolve__footer">
                <Button
                  type="primary"
                  loading={resolving}
                  onClick={handleResolve}
                  danger={selectedTone === 'destructive'}
                >
                  Confirm
                </Button>
                <Button onClick={() => setSelected(null)}>Cancel</Button>
              </div>
            </div>
          </>
        ) : null}
      </Drawer>
    </div>
  );
}
